import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useDispatch, useSelector } from "react-redux";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseRadio from "../../../components/ui/BaseRadio";
import { RadioGroup } from "@mui/material";
import HourlyPay from "./PayFormTypes/HourlyPay";
import HalfDayPay from "./PayFormTypes/HalfDayPay";
import {
  addExpense,
  addPurchaseOrder,
  removeExpense,
  removePurchaseOrder,
  resetPaymentFieldsAndValidation,
  updateExpense,
  updatePurchaseOrder,
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import { showToast } from "../../../store/toast/toastSlice";
import FullDayPay from "./PayFormTypes/FullDayPay";
import FixedPay from "./PayFormTypes/FixedPay";
import { CurrencySymbol, ModuleType, UserType, WorkOrderStatusType } from "../../../utils/enum";
import FTHPay from "./PayFormTypes/FTHPay";
import CustomHoursPay from "./PayFormTypes/CustomHoursPay";
import BaseDragDrop from "../../../components/ui/BaseDragDrop";
import BaseButton from "../../../components/ui/BaseButton";
import { useParams } from "react-router-dom";
import { deleteWorkOrderPurchaseOrder } from "../../../services/api/workOrder";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import useAuth from "../../../hooks/useAuth";
import PaymentTypeRadioLabel from "./PaymentTypeRadioLabel";
import FieldLabel from "../../../components/ui/FieldLabel";

const PayForm = forwardRef((props, ref) => {
  const { getPermissionForModule, authRole } = useAuth();
  const { hasUpdateAccess } = getPermissionForModule(ModuleType.WORK_ORDERS);
  // Customers manage their own (customer) rates but don't hold WORK_ORDERS access,
  // so surface the Customer Pay section for them too.
  const isCustomer = authRole === UserType.CUSTOMER;
  const canManagePayment = hasUpdateAccess || isCustomer;

  const dispatch = useDispatch();
  const payment = useSelector((state) => state.workOrder.payment);
  const currentStatus = props?.currentStatus;
  const customerStatus = props?.customerStatus;
  const disableRateFields = props?.disableRateFields ?? false;
  const disableExpenses = props?.disableExpenses ?? false;
  const allCurrencies = Object.keys(CurrencySymbol);
  const { id } = useParams();
  const [selectedPurchaseOrderFile, setSelectedPurchaseOrderFile] = useState(null);
  const [purchaseOrderRefs, setPurchaseOrderRefs] = useState([]);
  const [expenseFileRefs, setExpenseFileRefs] = useState([]);
  const [isDeletePOModalOpen, setIsDeletePOModalOpen] = useState(false);
  const [selectedPOToDelete, setSelectedPOToDelete] = useState(null);
  const workOrderFormValidation = useSelector((state) => state.workOrder.workOrderFormValidation);

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message, errorMsgFieldName) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
        if (errorMsgFieldName) {
          dispatch(updateWorkOrderFormValidation({ fieldName: errorMsgFieldName, data: message }));
        }
      };

      if (!disableRateFields && !payment.pay.currency) {
        fail("currency", "Currency is required");
      }

      if (!disableRateFields && payment.pay.paymentType === "HOURLY") {
        if (!payment.pay.hourlyRate || payment.pay.hourlyRate === "") {
          fail("hourlyRate", "Hourly rate is required", "hourlyRateErrorMsg");
        } else if (payment.pay.hourlyRate < 1) {
          fail("hourlyRate", "Hourly rate cannot be less than $1", "hourlyRateErrorMsg");
        }

        if (!payment.pay.minimumHours || payment.pay.minimumHours < 1) {
          fail("minimumHours", "Minimum 1 hour is required");
        }

        // Both of these live behind a checkbox and render disabled until it is
        // ticked — only demand a value once the user has opted in.
        if (
          payment.pay.enableClientAdditionalHourRate &&
          (!payment.pay.additionalHourlyRate || payment.pay.additionalHourlyRate < 1)
        ) {
          fail(
            "additionalHourlyRateError",
            "Additional hourly rate is required",
            "additionalHourlyRateError",
          );
        }

        if (
          payment.pay.enableNonBusinessHourRate &&
          (!payment.pay.nonBusinessHourRate || payment.pay.nonBusinessHourRate < 1)
        ) {
          fail("nonBusinessHourRate", "Non-business hour rate is required");
        }
      }

      if (!disableRateFields && payment.pay.paymentType === "HALF_DAY") {
        if (!payment.pay.halfDayRate) {
          fail("halfDayRate", "Half Day Rate is required", "halfDayRateErrorMsg");
        }
      }

      if (!disableRateFields && payment.pay.paymentType === "FULL_DAY") {
        if (!payment.pay.fullDayRate || payment.pay.fullDayRate <= 0) {
          fail("fullDayRate", "Full Day Rate is required", "fullDayRateErrorMsg");
        }
      }

      // An expense row is only worth saving once it says what it is and what it cost.
      (payment.expenses ?? []).forEach((expense, index) => {
        if (!expense.expenseType) {
          fail(`expenseType-${index}`, "Expense is required", `expenseType-${index}Msg`);
        }
        if (!expense.amount || Number(expense.amount) <= 0) {
          fail(`expenseAmount-${index}`, "Expense amount is required", `expenseAmount-${index}Msg`);
        }
      });

      const invalidPO = (payment.pay.purchaseOrders ?? []).some(
        (po) => po.attachment?.url && (!po.number || po.number.trim() === ""),
      );

      if (invalidPO) {
        fail(
          "purchaseOrders",
          "Each attached file must have a Purchase Order number",
          "purchaseOrdersErrorMsg",
        );
      }

      return { valid: errors.length === 0, errors };
    },
  }));
  useEffect(() => {
    setPurchaseOrderRefs((refs) =>
      Array(payment.pay.purchaseOrders?.length || 0)
        .fill()
        .map((_, i) => refs[i] || React.createRef()),
    );
  }, [payment.pay.purchaseOrders]);

  useEffect(() => {
    setExpenseFileRefs((refs) =>
      Array(payment.expenses?.length || 0)
        .fill()
        .map((_, i) => refs[i] || React.createRef()),
    );
  }, [payment.expenses]);

  useEffect(() => {
    if (props.isEditMode && props.paymentData) {
      const data = props.paymentData;
      if (data?.purchaseOrderFile) {
        setSelectedPurchaseOrderFile({
          file: data?.purchaseOrderFile,
          name: data?.purchaseOrderFile?.filename || "",
          url: data.purchaseOrderFile.url || null,
        });
      }
      dispatch(
        updateWorkOrderForm({
          step: "payment",
          data: {
            pay: {
              paymentTerms: data.paymentTerms || null,
              paymentType: data.paymentType || null,
              hourlyRate: data.hourlyRate || null,
              additionalHourlyRate: data.additionalHourlyRate || null,
              minimumHours: data.minimumHours || null,
              fixedAmount: data.fixedAmount || null,
              halfDayRate: data.halfDayRate || null,
              fullDayRate: data.fullDayRate || null,
              fthRate: data.fthRate || null,
              currency: data.currency || null,
              nonBusinessHourRate: data.nonBusinessHourRate || null,
              purchaseOrderNumber: data.purchaseOrderNumber || null,
              purchaseOrder: {
                files: data?.purchaseOrderFile || null,
              },
              purchaseOrders: Array.isArray(data.purchaseOrders) ? data.purchaseOrders : [],
              customHours: data.customHours || null,
              customHoursRate: data.customHoursRate || null,
              enableClientAdditionalHourRate: !!data.additionalHourlyRate ?? false,
              enableNonBusinessHourRate: data.nonBusinessHourRate ? true : false,
            },
            expenses: Array.isArray(data.expenses) ? data.expenses : [],
          },
        }),
      );
    }
  }, [props.isEditMode, props.paymentData, dispatch]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name !== "currency" && name !== "purchaseOrderNumber") {
      dispatch(resetPaymentFieldsAndValidation());
    } else if (name === "currency") {
      // Currency deliberately skips the blanket reset above (it must not wipe the
      // rates), so clear its own error here or it stays red after being picked.
      dispatch(updateWorkOrderFormValidation({ fieldName: "currency", data: false }));
    }
    const enableCustomRates =
      name === "paymentType" && value === "FIXED" ? true : payment.pay.enableCustomRates;

    dispatch(
      updateWorkOrderForm({
        step: "payment",
        data: {
          pay: {
            ...payment.pay,
            [name]: value,
            enableCustomRates,
          },
        },
      }),
    );
  };

  const handleExpenseChange = (index, event) => {
    const { name, value } = event.target;
    // Clear this row's own error once it is answered, so the field doesn't stay red.
    if (name === "expenseType" || name === "amount") {
      const errorField = name === "amount" ? `expenseAmount-${index}` : `expenseType-${index}`;
      dispatch(updateWorkOrderFormValidation({ fieldName: errorField, data: false }));
    }
    dispatch(updateExpense({ index, field: name, value }));
  };
  const clearExpenseErrors = () => {
    (payment.expenses ?? []).forEach((_, index) => {
      dispatch(updateWorkOrderFormValidation({ fieldName: `expenseType-${index}`, data: false }));
      dispatch(updateWorkOrderFormValidation({ fieldName: `expenseAmount-${index}`, data: false }));
    });
  };

  const handleAddExpense = () => {
    clearExpenseErrors();
    dispatch(addExpense());
  };

  const handleRemoveExpense = (id) => {
    clearExpenseErrors();
    dispatch(removeExpense(id));
  };
  const handleExpenseFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!payment.expenses[index]?.expenseKey) {
      dispatch(updateExpense({ index, field: "expenseKey", value: crypto.randomUUID() }));
    }

    const fileWithUrl = {
      fileName: file.name,
      url: URL.createObjectURL(file),
    };

    dispatch(updateExpense({ index, field: "attachment", value: fileWithUrl }));
  };
  const handleRemovePurchaseOrder = (id) => {
    dispatch(removePurchaseOrder(id));
  };
  const handlePurchaseOrderChange = (index, field, value) => {
    dispatch(updatePurchaseOrder({ index, field, value }));
  };

  const handleAddPurchaseOrder = () => {
    dispatch(addPurchaseOrder());
  };
  const handlePurchaseOrderFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileWithUrl = {
      fileName: file.name,
      url: URL.createObjectURL(file),
    };

    handlePurchaseOrderChange(index, "attachment", fileWithUrl);
  };

  const [isDeletingPO, setIsDeletingPO] = useState(false);

  const isPersistedPO = (po) => !!id && typeof po?.id === "string";

  const handleConfirmDeletePO = async () => {
    if (!selectedPOToDelete) return;

    try {
      setIsDeletingPO(true);
      const poId = selectedPOToDelete.id;

      if (isPersistedPO(selectedPOToDelete)) {
        await deleteWorkOrderPurchaseOrder(id, poId);
      }
      dispatch(removePurchaseOrder(poId));
      // Absent in the create/edit wizard, which renders this form without one.
      props.queryClient?.invalidateQueries(["jobDetail", id]);
      dispatch(showToast({ message: "Purchase Order deleted successfully", type: "success" }));
    } catch (error) {
      console.error("Failed to delete purchase order:", error);
      dispatch(showToast({ message: "Failed to delete Purchase Order", type: "error" }));
    } finally {
      setIsDeletingPO(false);
      setIsDeletePOModalOpen(false);
      setSelectedPOToDelete(null);
    }
  };

  const openDeletePOModal = (po) => {
    setSelectedPOToDelete(po);
    setIsDeletePOModalOpen(true);
  };

  const canEditPayment =
    ![WorkOrderStatusType.PAID].includes(currentStatus) ||
    [WorkOrderStatusType.APPROVED, WorkOrderStatusType.INVOICED].includes(customerStatus);

  return (
    <BaseCard className="pt-4 pb-6 xl:px-16 px-5 mt-1 rounded-lg" elevation={1}>
      {canEditPayment && canManagePayment && (
        <>
          <div className="border-bottom mb-4">
            <h6 className="mb-2 text-dark">{isCustomer ? "Payment" : "Customer Pay"}</h6>
          </div>

          <div className="grid grid-cols-8 gap-x-10 gap-y-6 items-center xl:mb-5 mb-2 mt-5">
            <p className="font-normal 2xl:col-span-1 xl:col-span-2 lg:col-span-2 col-span-9">
              Payment Type
            </p>
            <div className="xl:col-span-3 lg:col-span-4  col-span-6">
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                value={payment.pay.paymentType}
                name="radio-buttons-group"
              >
                <div>
                  {/* <BaseRadio
                label="FTH"
                value="FTH"
                name="paymentType"
                onChange={handleChange}
              /> */}

                  <BaseRadio
                    label={<PaymentTypeRadioLabel type="HOURLY" />}
                    value="HOURLY"
                    name="paymentType"
                    onChange={handleChange}
                    disabled={disableRateFields}
                  />
                  <BaseRadio
                    label={<PaymentTypeRadioLabel type="CUSTOM_HOURS" />}
                    value="CUSTOM_HOURS"
                    name="paymentType"
                    onChange={handleChange}
                    disabled={disableRateFields}
                  />
                  <BaseRadio
                    label={<PaymentTypeRadioLabel type="HALF_DAY" />}
                    value="HALF_DAY"
                    name="paymentType"
                    onChange={handleChange}
                    disabled={disableRateFields}
                  />

                  <BaseRadio
                    label={<PaymentTypeRadioLabel type="FULL_DAY" />}
                    value="FULL_DAY"
                    name="paymentType"
                    onChange={handleChange}
                    disabled={disableRateFields}
                  />

                  <BaseRadio
                    label={<PaymentTypeRadioLabel type="FIXED" />}
                    value="FIXED"
                    name="paymentType"
                    onChange={handleChange}
                    disabled={disableRateFields}
                  />

                  {/* <BaseRadio
                  label="Blended"
                  value="BLENDED"
                  name="paymentType"
                  onChange={handleChange}
                /> */}
                </div>
              </RadioGroup>
            </div>
          </div>
        </>
      )}
      <div className="grid grid-cols-2 gap-y-4 mt-3">
        {canEditPayment && canManagePayment && (
          <>
            {" "}
            <div className="lg:mr-10 lg:col-span-1 col-span-2 ">
              {/* Hand-rolled label rather than FieldLabel, so the required marker
                  is spelled out here to match what FieldLabel renders. */}
              <p className="font-normal 2xl:col-span-1 xl:col-span-2 lg:col-span-2 col-span-9 mb-1">
                Currency:
                <span className="text-red-500 ml-0.5" aria-hidden="true">
                  *
                </span>
              </p>
              <BaseAutoSelect
                required
                fullHeight={true}
                name="currency"
                disabled={disableRateFields}
                onChange={handleChange}
                options={allCurrencies.map((currency) => ({
                  label: currency,
                  value: currency,
                }))}
                value={payment?.pay?.currency}
                error={workOrderFormValidation.currency}
                errorMsg="Currency is required"
              />
            </div>
            <BaseTextField
              disabled={disableRateFields || !payment.pay.enableNonBusinessHourRate}
              onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
              error={workOrderFormValidation.nonBusinessHourRateError}
              errorMsg={workOrderFormValidation.nonBusinessHourRateError}
              fullWidth
              fullHeight={false}
              id="nonBusinessHourRate"
              name="nonBusinessHourRate"
              label={
                <div className="flex">
                  <p className="font-normal mr-2">
                    NBH
                    <span className="text-[10px]"> (None-Business Hour) </span>
                    Rate
                    {/* Only demanded once the checkbox opts the rate in. */}
                    {payment.pay.enableNonBusinessHourRate && (
                      <span className="text-red-500 ml-0.5" aria-hidden="true">
                        *
                      </span>
                    )}
                  </p>{" "}
                  <BaseCheckbox
                    id="enableNonBusinessHourRate"
                    name="enableNonBusinessHourRate"
                    disabled={disableRateFields}
                    checked={payment.pay.enableNonBusinessHourRate || false}
                    onChange={() => {
                      if (payment.pay.enableNonBusinessHourRate) {
                        dispatch(
                          updateWorkOrderFormValidation({
                            fieldName: "nonBusinessHourRateError",
                            data: "",
                          }),
                        );
                      }
                      if (
                        !payment.pay.enableNonBusinessHourRate &&
                        payment.pay.nonBusinessHourRate <= 0
                      ) {
                        dispatch(
                          updateWorkOrderFormValidation({
                            fieldName: "nonBusinessHourRateError",
                            data: `Minimum NBH Rate is ${
                              CurrencySymbol[payment.pay.currency]
                                ? CurrencySymbol[payment.pay.currency]
                                : "$"
                            } ${"  "}`,
                          }),
                        );
                      }
                      dispatch(
                        updateWorkOrderForm({
                          step: "payment",
                          data: {
                            pay: {
                              ...payment,
                              enableNonBusinessHourRate: !payment.pay.enableNonBusinessHourRate,
                            },
                          },
                        }),
                      );
                    }}
                  />
                </div>
              }
              optional={false}
              labelShrink={false}
              value={payment.pay.nonBusinessHourRate ?? ""}
              onChange={handleChange}
              isDefaultField={false}
              type="float"
            />
          </>
        )}
      </div>

      {canEditPayment && canManagePayment && (
        <>
          {payment.pay.paymentType === "HOURLY" && <HourlyPay disabled={disableRateFields} />}
          {payment.pay.paymentType === "FTH" && <FTHPay disabled={disableRateFields} />}
          {payment.pay.paymentType === "FULL_DAY" && <FullDayPay disabled={disableRateFields} />}
          {payment.pay.paymentType === "HALF_DAY" && <HalfDayPay disabled={disableRateFields} />}
          {payment.pay.paymentType === "CUSTOM_HOURS" && (
            <CustomHoursPay disabled={disableRateFields} />
          )}
          {payment.pay.paymentType === "FIXED" && <FixedPay disabled={disableRateFields} />}
          {/* {payment.pay.paymentType === "BLENDED" && <BlendedPay />} */}

          <div>
            {payment.expenses?.map((expense, index) => (
              <div className="bg-light rounded-lg pt-4 pb-8 lg:px-10 px-4 mt-10">
                {!disableExpenses && (
                  <div className="flex justify-end">
                    <i
                      className="fa-regular fa-trash-can cursor-pointer"
                      onClick={() => handleRemoveExpense(expense?.id)}
                    ></i>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <div className="lg:col-span-1 col-span-2">
                    <BaseAutoSelect
                      fullHeight={false}
                      label="Expense"
                      name="expenseType"
                      labelShrink={false}
                      requirement="required"
                      isDefaultField={false}
                      disabled={disableExpenses}
                      error={!!workOrderFormValidation[`expenseType-${index}`]}
                      errorMsg={workOrderFormValidation[`expenseType-${index}Msg`]}
                      onChange={(e) => handleExpenseChange(index, e)}
                      options={[
                        { label: "Travel Expense", value: "TRAVEL" },
                        { label: "Tools Expense", value: "TOOLS" },
                        { label: "Material Expense", value: "MATERIAL" },
                        { label: "All Options", value: "ALL" },
                        { label: "Other", value: "OTHER" },
                      ]}
                      defaultValue={expense.expenseType}
                      value={expense.expenseType}
                    />
                  </div>
                  <div className="lg:col-span-1 col-span-2">
                    <BaseTextField
                      fullHeight={false}
                      label="Description"
                      name="description"
                      labelShrink={false}
                      required={true}
                      isDefaultField={false}
                      optional={false}
                      disabled={disableExpenses}
                      value={expense.description}
                      onChange={(e) => handleExpenseChange(index, e)}
                    />
                  </div>
                  <div className="lg:col-span-1 col-span-2">
                    <BaseTextField
                      fullHeight={false}
                      label="Amount"
                      name="amount"
                      labelShrink={false}
                      type="float"
                      requirement="required"
                      isDefaultField={false}
                      disabled={disableExpenses}
                      error={!!workOrderFormValidation[`expenseAmount-${index}`]}
                      errorMsg={workOrderFormValidation[`expenseAmount-${index}Msg`]}
                      value={expense.amount}
                      onChange={(e) => handleExpenseChange(index, e)}
                    />
                  </div>
                  <div className="lg:col-span-1 col-span-2">
                    {/* Same renderer as the Amount/Description labels above, so
                        the optional marker matches them exactly. */}
                    <FieldLabel label="Attachment" requirement="optional" fullHeight={false} />

                    <input
                      type="file"
                      ref={expenseFileRefs[index]}
                      onChange={(e) => handleExpenseFileChange(index, e)}
                      style={{ display: "none" }}
                    />

                    <div
                      style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                      className="w-full flex h-[47px] justify-between items-center px-3 py-1 rounded-md"
                    >
                      <div className="flex-1 min-w-0 rounded-md flex items-center">
                        {expense.attachment ? (
                          <>
                            {expense.attachment.url ? (
                              <a
                                href={expense.attachment.url}
                                download={expense.attachment.fileName}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="py-1 cursor-pointer truncate min-w-0 text-blue-500 hover:underline font-300"
                              >
                                {expense.attachment.fileName}
                              </a>
                            ) : (
                              <p className="py-1 font-300 truncate min-w-0">
                                {expense.attachment.fileName}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-gray-400 py-1 ml-2">No file selected</p>
                        )}
                      </div>

                      {/* Auto width + nowrap: the old fixed 80px broke the label
                          onto two lines, which overflowed the 47px-tall row. */}
                      {!disableExpenses && (
                        <BaseButton
                          variant="outlined"
                          color="primary"
                          className="h-[32px] ml-3 shrink-0 whitespace-nowrap !px-3"
                          onClick={() => expenseFileRefs[index]?.current?.click()}
                        >
                          Choose File
                        </BaseButton>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {!disableExpenses && (
              <div className="inline-block cursor-pointer" onClick={handleAddExpense}>
                <div className="flex items-center mt-4">
                  <i className="fa-solid fa-plus text-secondary"></i>
                  <p className="text-secondary text-sm ml-1">Add Expenses</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      <>
        {payment.pay.purchaseOrders?.map((po, index) => {
          return (
            <div className="bg-light rounded-lg pt-4 pb-8 lg:px-10 px-4 mt-10" key={po.id}>
              <div className="flex justify-end">
                <i
                  className="fa-regular fa-trash-can cursor-pointer"
                  onClick={() =>
                    isPersistedPO(po) ? openDeletePOModal(po) : handleRemovePurchaseOrder(po.id)
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                <div className="lg:col-span-1 col-span-2">
                  <BaseTextField
                    labelShrink={false}
                    optional={false}
                    isDefaultField={false}
                    fullHeight={false}
                    label="Purchase Order Number"
                    name="number"
                    value={po.number}
                    onChange={(e) => handlePurchaseOrderChange(index, "number", e.target.value)}
                  />
                </div>
                <div className="lg:col-span-1 col-span-2">
                  <p className="font-normal mb-2">Purchase Order File</p>

                  <input
                    type="file"
                    ref={purchaseOrderRefs[index]}
                    onChange={(e) => handlePurchaseOrderFileChange(index, e)}
                    style={{ display: "none" }}
                  />

                  <div
                    style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                    className="w-full flex h-[47px] justify-between items-center px-3 py-1 rounded-md"
                  >
                    <div className="w-full rounded-md flex flex-wrap">
                      {po.attachment ? (
                        po.attachment.url ? (
                          <a
                            href={po.attachment.url}
                            download={po.attachment.fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="py-1 cursor-pointer block text-blue-500 hover:underline font-300"
                          >
                            {po.attachment.fileName}
                          </a>
                        ) : (
                          <p className="py-1 font-300">{po.attachment.fileName}</p>
                        )
                      ) : (
                        <p className="text-gray-400 py-1 ml-2">No file selected</p>
                      )}
                    </div>

                    <BaseButton
                      variant="outlined"
                      color="primary"
                      className="h-[32px] ml-3 shrink-0 whitespace-nowrap !px-3"
                      onClick={() => purchaseOrderRefs[index].current.click()}
                    >
                      Choose File
                    </BaseButton>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="inline-block cursor-pointer" onClick={handleAddPurchaseOrder}>
          <div className="flex items-center mt-4">
            <i className="fa-solid fa-plus text-secondary"></i>
            <p className="text-secondary text-sm ml-1">Add Purchase Order</p>
          </div>
        </div>
        {isDeletePOModalOpen && (
          <BaseConfirmModal
            open={isDeletePOModalOpen}
            onClose={() => setIsDeletePOModalOpen(false)}
            cancelText="Cancel"
            confirmText="Delete"
            confirmButtonColor="error"
            isLoading={isDeletingPO}
            disabled={isDeletingPO}
            title="Delete Purchase Order"
            description={
              <p>
                Are you sure you want to delete the purchase order
                <span className="text-red-600 font-medium mx-1">
                  "{selectedPOToDelete?.number}"
                </span>
                ? This action cannot be undone.
              </p>
            }
            onConfirm={handleConfirmDeletePO}
          />
        )}
      </>
    </BaseCard>
  );
});

export default PayForm;
